import { CommandBarButton, IContextualMenuProps } from "@fluentui/react";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import clsx from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import { accent } from "../../../config/colors";
import { useSelector } from "../../../store/hooks";
import { transformLocationsForExcel } from "../../../utils/excel";
import { createPDFLocations } from "../../../utils/pdf";
import { TableSpinner } from "../../../components/TableSpinner";

type Props = {
  refExcelExport: ExcelExport;
  filteredLocations: any[];
  visibleLabelKeys?: string[];
};

export const ExportMenu = ({
  refExcelExport,
  filteredLocations,
  visibleLabelKeys,
}: Props) => {
  const { t } = useTranslation();
  const taxonomy = useSelector((s) => s.taxonomy);
  const [isPDFLoading, setIsPDFLoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const isAnyLoading = isPDFLoading || isExcelLoading;

  const handleExportPDF = () => {
    setIsPDFLoading(true);
    try {
      createPDFLocations({
        locations: filteredLocations,
        taxonomy,
        t,
        visibleLabelKeys,
      }).open();
    } finally {
      setIsPDFLoading(false);
    }
  };

  const handleExportExcel = () => {
    setIsExcelLoading(true);
    try {
      const data = transformLocationsForExcel({
        locations: filteredLocations,
        t,
        taxonomy,
      });
      refExcelExport?.save(data);
    } finally {
      setIsExcelLoading(false);
    }
  };

  const menuProps: IContextualMenuProps = {
    items: [
      {
        key: "exportPDF",
        text: t("greco.exportPDF"),
        iconProps: {
          iconName: isPDFLoading ? "ProgressRingDots" : "PDF",
          style: { color: accent.red },
        },
        disabled: isPDFLoading,
        onClick: handleExportPDF,
      },
      {
        key: "exportExcel",
        text: "Export Excel",
        iconProps: {
          iconName: isExcelLoading ? "ProgressRingDots" : "ExcelDocument",
          style: { color: "green" },
        },
        disabled: isExcelLoading,
        onClick: handleExportExcel,
      },
    ],
  };

  return (
    <>
      <StyledCommandBarButton
        text="Export"
        iconProps={{ iconName: "Export" }}
        menuProps={menuProps}
        disabled={isAnyLoading}
      />
      {isAnyLoading && <TableSpinner />}
    </>
  );
};

const spin = keyframes`
  from {
    transform: rotateZ(0deg)
  }
  to {
    transform: rotateZ(360deg)
  }
`;

const StyledCommandBarButton = styled(CommandBarButton)`
  height: 32px;
  &.isLoading {
    i {
      animation: ${spin} 500ms linear infinite;
    }
  }
`;
